/**
 * Backend abstraction layer — public entry point.
 *
 * `getBackend(state)` returns the ViewerBackend adapter for the current product
 * mode. Call sites depend on this, never on a concrete adapter, so adding the
 * cloud/EEN adapters (Chunks 3/5) is a switch arm here — not an `if (cloud)`
 * sprinkled across components.
 */
import { PRODUCT_MODE, getProductMode } from './productMode';
import { LocalNvrAdapter } from './LocalNvrAdapter';
import { CloudNvrAdapter } from './CloudNvrAdapter';

// The local adapter is stateless (session/identity live in Redux), so one
// shared instance is fine and avoids per-call allocation. The cloud adapter
// holds the JWT/device index on the instance, so it MUST be a singleton too —
// the token set at login has to be visible to later URL-builder calls.
let _localAdapter = null;
let _cloudAdapter = null;

/**
 * @param {Object} [state] - Redux state, used to resolve product mode
 * @returns {import('./ViewerBackend').ViewerBackend}
 */
export const getBackend = (state) => {
  const mode = getProductMode(state);
  switch (mode) {
    case PRODUCT_MODE.LOCAL:
      if (!_localAdapter) _localAdapter = new LocalNvrAdapter();
      return _localAdapter;
    case PRODUCT_MODE.CLOUD:
      if (!_cloudAdapter) _cloudAdapter = new CloudNvrAdapter();
      return _cloudAdapter;
    case PRODUCT_MODE.EEN:
      throw new Error(`[backend] No adapter for product mode "${mode}" yet`);
    default:
      throw new Error(`[backend] Unknown product mode "${mode}"`);
  }
};

/** Test seam: drop the cached adapters so each test gets a fresh instance. */
export const _resetBackendsForTesting = () => {
  _localAdapter = null;
  _cloudAdapter = null;
};

export { PRODUCT_MODE, getProductMode } from './productMode';
export { getProductConfig } from './productConfig';
export { ViewerBackend } from './ViewerBackend';
export { LocalNvrAdapter } from './LocalNvrAdapter';
export { CloudNvrAdapter } from './CloudNvrAdapter';
export { veCall, getCloudApiBase, setCloudApiBase, veUrl } from './veCall';
export {
  classifyDevice,
  getEngineBackend,
  buildCloudLiveUrl,
  buildCloudClipUrl,
  buildCloudThumbUrl,
  buildCloudMetadataUrl,
} from './cloudUrls';
export { jsonRpcCall, rpcOk, rpcPayload } from './jsonRpcCall';
